import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Clock, CheckCircle, Send, XCircle, RotateCcw, MessageCircleWarning } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ResponseETA from '../components/ResponseETA';
import { toast } from 'sonner';

export default function ConsultationDetail() {
  const { consultationId } = useParams();
  const [consultation, setConsultation] = useState(null);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundNote, setRefundNote] = useState('');
  const [refunding, setRefunding] = useState(false);
  // Client-initiated refund request
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requesting, setRequesting] = useState(false);
  // Admin deny modal
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyNote, setDenyNote] = useState('');
  const [denying, setDenying] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadConsultation();
  }, [consultationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConsultation = async () => {
    try {
      const { data } = await api.get(`/consultations/${consultationId}`);
      setConsultation(data);
      setResponse(data.response || '');
    } catch (error) {
      toast.error('Failed to load consultation');
      navigate('/consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/consultations/${consultationId}/respond`, {
        response_text: response
      });
      toast.success('Response sent successfully');
      loadConsultation();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefund = async () => {
    setRefunding(true);
    try {
      // If there's a pending user request, approve it. Otherwise, direct refund.
      const endpoint = consultation.refund_status === 'requested'
        ? `/admin/consultations/${consultationId}/refund-approve`
        : `/admin/consultations/${consultationId}/refund`;
      const { data } = await api.post(endpoint, { note: refundNote.trim() || null });
      toast.success(`Refund of ${data.amount} issued — an email is on its way.`);
      setShowRefundModal(false);
      setRefundNote('');
      loadConsultation();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const submitRefundRequest = async () => {
    const reason = requestReason.trim();
    if (!reason) { toast.error('Please share a short reason'); return; }
    setRequesting(true);
    try {
      await api.post(`/consultations/${consultationId}/refund-request`, { reason });
      toast.success('Refund request submitted — Genesis will review it soon.');
      setShowRequestModal(false);
      setRequestReason('');
      loadConsultation();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not submit request');
    } finally {
      setRequesting(false);
    }
  };

  const handleDenyRequest = async () => {
    setDenying(true);
    try {
      await api.post(`/admin/consultations/${consultationId}/refund-deny`, { note: denyNote.trim() || null });
      toast.success('Refund request denied — user has been notified.');
      setShowDenyModal(false);
      setDenyNote('');
      loadConsultation();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not deny request');
    } finally {
      setDenying(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }}></div>
          <p className="mt-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!consultation) return null;

  const getStatusIcon = () => {
    if (consultation.status === 'completed') return <CheckCircle className="w-6 h-6" style={{ color: '#78A18E' }} />;
    if (consultation.status === 'refunded') return <RotateCcw className="w-6 h-6" style={{ color: '#8C867D' }} />;
    return <Clock className="w-6 h-6" style={{ color: '#E8C265' }} />;
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <div className="max-w-4xl mx-auto px-6 py-16">
        <button
          onClick={() => navigate('/consultations')}
          className="flex items-center gap-2 mb-8 px-4 py-2 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
          style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }}
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Consultations</span>
        </button>

        <div className="p-8 rounded-3xl mb-8" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{consultation.title}</h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  {getStatusIcon()}
                  <span className="font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29', fontSize: '14px' }}>
                    {consultation.status === 'pending_payment' ? 'Pending Payment' : consultation.status.charAt(0).toUpperCase() + consultation.status.slice(1)}
                  </span>
                </div>
                <span style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', fontSize: '14px' }}>
                  {new Date(consultation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>
            <div className="px-4 py-2 rounded-full" style={{ backgroundColor: '#E8C26520', color: '#E8C265', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>
              {consultation.tier}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Your Request</h2>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: '1.8' }}>{consultation.description}</p>
          </div>

          {consultation.response && (
            <div className="p-6 rounded-2xl" style={{ backgroundColor: '#78A18E10', border: '2px solid #78A18E40' }}>
              <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Guidance Response</h2>
              <div style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: '2', whiteSpace: 'pre-wrap' }}>
                {consultation.response}
              </div>
              {consultation.responded_at && (
                <p className="mt-4 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E' }}>
                  Responded on {new Date(consultation.responded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>
          )}

          {consultation.status === 'refunded' && (
            <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: '#8C867D15', border: '2px solid #8C867D40' }} data-testid="refunded-banner">
              <div className="flex items-start gap-3">
                <RotateCcw className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#8C867D' }} />
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Refunded</h3>
                  <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                    This consultation was cancelled and a full refund was issued
                    {consultation.refunded_at ? ` on ${new Date(consultation.refunded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}.
                  </p>
                  {consultation.refund_note && (
                    <p className="text-sm mt-2 italic" style={{ fontFamily: 'Lora, serif', color: '#595553' }}>
                      "{consultation.refund_note}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Client sees: their refund request is pending */}
          {consultation.refund_status === 'requested' && consultation.status !== 'refunded' && user?.role !== 'admin' && (
            <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: '#E8C26515', border: '2px solid #E8C26540' }} data-testid="refund-pending-user">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#B8901A' }} />
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Refund Request Submitted</h3>
                  <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Genesis will review your request and get back to you soon.</p>
                </div>
              </div>
            </div>
          )}

          {/* Client sees: refund was denied */}
          {consultation.refund_status === 'denied' && user?.role !== 'admin' && (
            <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: '#8C867D15', border: '2px solid #8C867D40' }} data-testid="refund-denied-user">
              <div className="flex items-start gap-3">
                <MessageCircleWarning className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#8C867D' }} />
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Refund request was not approved</h3>
                  {consultation.refund_denial_note && (
                    <p className="text-sm mt-2 italic" style={{ fontFamily: 'Lora, serif', color: '#595553' }}>
                      "{consultation.refund_denial_note}"
                    </p>
                  )}
                  <p className="text-xs mt-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Your consultation response will still be prepared and sent.</p>
                </div>
              </div>
            </div>
          )}

          {/* Admin sees: pending refund request from user */}
          {consultation.refund_status === 'requested' && consultation.status !== 'refunded' && user?.role === 'admin' && (
            <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: '#E8C26520', border: '2px solid #E8C26580' }} data-testid="refund-request-admin">
              <div className="flex items-start gap-3 mb-4">
                <MessageCircleWarning className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#B8901A' }} />
                <div className="flex-1">
                  <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Refund Request from {consultation.user_name}</h3>
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    Requested {consultation.refund_requested_at ? new Date(consultation.refund_requested_at).toLocaleString() : ''}
                  </p>
                </div>
              </div>
              {consultation.refund_request_reason && (
                <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF' }}>
                  <p className="text-xs mb-2 font-semibold uppercase tracking-wide" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Their reason</p>
                  <p style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{consultation.refund_request_reason}</p>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowRefundModal(true)}
                  className="flex items-center gap-2 px-5 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#78A18E', color: '#FDFBF7', fontWeight: 600, fontSize: '14px' }}
                  data-testid="approve-refund-btn"
                >
                  <CheckCircle className="w-4 h-4" /> Approve & Refund
                </button>
                <button
                  onClick={() => setShowDenyModal(true)}
                  className="flex items-center gap-2 px-5 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A4415', color: '#E57A44', fontWeight: 600, fontSize: '14px' }}
                  data-testid="deny-refund-btn"
                >
                  <XCircle className="w-4 h-4" /> Deny
                </button>
              </div>
            </div>
          )}

          {/* Client: request refund button (paid, not refunded, no pending request) */}
          {user?.role !== 'admin'
            && consultation.payment_status === 'paid'
            && consultation.status !== 'refunded'
            && !['requested', 'denied'].includes(consultation.refund_status) && (
            <div className="p-4 rounded-2xl mb-6 flex items-center justify-between gap-3 flex-wrap" style={{ backgroundColor: '#F3EFE7' }}>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                Something not sitting right? You can request a refund.
              </p>
              <button
                onClick={() => setShowRequestModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', color: '#595553', fontWeight: 600, fontSize: '13px', border: '1px solid #E6E2D8' }}
                data-testid="user-request-refund-btn"
              >
                <RotateCcw className="w-4 h-4" /> Request Refund
              </button>
            </div>
          )}

          {!consultation.response && consultation.status === 'pending' && (
            <ResponseETA consultation={consultation} />
          )}

          {!consultation.response && consultation.status === 'pending_payment' && (
            <div className="p-6 rounded-2xl" style={{ backgroundColor: '#5B7C9910' }}>
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                Payment pending. Please complete the payment to submit your consultation request.
              </p>
            </div>
          )}
        </div>

        {user?.role === 'admin' && consultation.status === 'pending' && (
          <div className="p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Provide Response</h2>
            <form onSubmit={handleRespond} className="space-y-4">
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                required
                rows={12}
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Write your personalized guidance here..."
                data-testid="admin-response"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                  data-testid="submit-response"
                >
                  <Send className="w-5 h-5" />
                  <span>{submitting ? 'Sending...' : 'Send Response'}</span>
                </button>
                {consultation.payment_status === 'paid' && (
                  <button
                    type="button"
                    onClick={() => setShowRefundModal(true)}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
                    style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#8C867D15', color: '#8C867D', fontWeight: 600 }}
                    data-testid="open-refund-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Cancel & Refund</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {user?.role === 'admin' && consultation.status === 'completed' && consultation.payment_status === 'paid' && (
          <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                Need to cancel this consultation and refund the user?
              </p>
              <button
                onClick={() => setShowRefundModal(true)}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-1"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#8C867D15', color: '#8C867D', fontWeight: 600, fontSize: '14px' }}
                data-testid="open-refund-btn-completed"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Issue Refund</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {showRefundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(44, 42, 41, 0.6)' }}>
          <div className="w-full max-w-md p-8 rounded-3xl" style={{ backgroundColor: '#FDFBF7' }} data-testid="refund-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{consultation.refund_status === 'requested' ? 'Approve & Refund' : 'Issue Refund'}</h2>
              <button onClick={() => setShowRefundModal(false)} className="p-2 rounded-full hover:bg-gray-100" data-testid="refund-close"><XCircle className="w-6 h-6" style={{ color: '#595553' }} /></button>
            </div>
            <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: '#E57A4415' }}>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
                This will refund the full amount back to <strong>{consultation.user_name}</strong> via Stripe and mark this consultation as cancelled. This cannot be undone.
              </p>
            </div>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
              Note to include in the refund email <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              value={refundNote}
              onChange={(e) => setRefundNote(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="A short, kind message to include (e.g., 'This was better suited for a different type of guidance.')"
              className="w-full px-4 py-3 rounded-xl border"
              style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
              data-testid="refund-note"
            />
            <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{refundNote.length} / 400</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleRefund}
                disabled={refunding}
                className="flex-1 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="confirm-refund"
              >
                <RotateCcw className="w-4 h-4" />
                <span>{refunding ? 'Processing…' : 'Confirm Refund'}</span>
              </button>
              <button
                onClick={() => setShowRefundModal(false)}
                className="px-6 py-3 rounded-full"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553', fontWeight: 600 }}
                data-testid="cancel-refund"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(44, 42, 41, 0.6)' }}>
          <div className="w-full max-w-md p-8 rounded-3xl" style={{ backgroundColor: '#FDFBF7' }} data-testid="request-refund-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Request Refund</h2>
              <button onClick={() => setShowRequestModal(false)} className="p-2 rounded-full hover:bg-gray-100"><XCircle className="w-6 h-6" style={{ color: '#595553' }} /></button>
            </div>
            <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: '#F3EFE7' }}>
              <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
                Your request will go to Genesis for review. Refunds are approved case-by-case — you'll receive an email either way.
              </p>
            </div>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
              Why are you requesting a refund? <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              rows={5}
              maxLength={800}
              placeholder="Share a short reason so Genesis can consider your request thoughtfully…"
              className="w-full px-4 py-3 rounded-xl border"
              style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29', lineHeight: 1.6 }}
              data-testid="request-refund-reason"
            />
            <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{requestReason.length} / 800</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={submitRefundRequest}
                disabled={requesting || !requestReason.trim()}
                className="flex-1 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="submit-refund-request"
              >
                <Send className="w-4 h-4" />
                <span>{requesting ? 'Sending…' : 'Send Request'}</span>
              </button>
              <button
                onClick={() => setShowRequestModal(false)}
                className="px-6 py-3 rounded-full"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553', fontWeight: 600 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDenyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(44, 42, 41, 0.6)' }}>
          <div className="w-full max-w-md p-8 rounded-3xl" style={{ backgroundColor: '#FDFBF7' }} data-testid="deny-refund-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Deny Refund Request</h2>
              <button onClick={() => setShowDenyModal(false)} className="p-2 rounded-full hover:bg-gray-100"><XCircle className="w-6 h-6" style={{ color: '#595553' }} /></button>
            </div>
            <p className="text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
              <strong>{consultation.user_name}</strong> will receive a warm email explaining the decision. The consultation will remain active.
            </p>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
              Note to include in the email <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional but kind)</span>
            </label>
            <textarea
              value={denyNote}
              onChange={(e) => setDenyNote(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="A short message explaining why (e.g., 'The response was already prepared…')"
              className="w-full px-4 py-3 rounded-xl border"
              style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
              data-testid="deny-note"
            />
            <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{denyNote.length} / 500</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleDenyRequest}
                disabled={denying}
                className="flex-1 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="confirm-deny"
              >
                <XCircle className="w-4 h-4" />
                <span>{denying ? 'Sending…' : 'Deny & Notify'}</span>
              </button>
              <button
                onClick={() => setShowDenyModal(false)}
                className="px-6 py-3 rounded-full"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553', fontWeight: 600 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      <Footer />
    </div>
  );
}
